import React from 'react';
import styled from 'styled-components';
import { isBefore, isAfter, parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Col, Container, Row } from 'components/atoms/Grid';
import Colors from 'components/tokens/Colors';
import Text from 'components/atoms/Text';
import Button from 'components/atoms/Button';
import Slider, { SlideItem } from 'components/organisms/Slider';

import { sbEditable } from '@storyblok/storyblok-editable';
import { useState } from 'react';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/client';
import RouterLink from 'components/atoms/RouterLink';
import { useEditor } from 'providers/generalContext';
import { render } from 'storyblok-rich-text-react-renderer';
import { renderColor } from '../HeaderEducationArticle';

const query = gql`
    query {
        PageItem(id: "educacao/eventos") {
            content {
                body
            }
        }
    }
`;

export function handleButtonContent(startDate, endDate) {
    const today = new Date();

    if (isBefore(new Date(startDate), today) && isAfter(new Date(endDate), today)) {
        return 'Mais informações';
    }

    if (isBefore(new Date(endDate), today)) {
        return 'Inscrições encerradas';
    }

    if (!startDate || isAfter(new Date(startDate), today)) {
        return 'Inscrições em breve';
    }
}

export default function Calendar({ mainColor, contexto, ...props }) {
    const [clickedSlider, setClickedSlider] = useState(0);

    const { loading, data } = useQuery(query);
    const content = (!loading && data?.PageItem?.content?.body) || [];
    const { isStoryblokEditor } = useEditor();

    return (
        <MainContent {...sbEditable(props)}>
            <Container style={{ position: 'relative' }}>
                <Row pt="8" pb="8 9">
                    <Col mb="5" size="12" px="1.5 4">
                        <Row justifyContent="space-between" alignItems="center">
                            <Col size="12 auto">
                                <Text size="48 56" lineHeight="40 48" title>
                                    {props?.title}
                                </Text>
                            </Col>
                            <Col size="hidden auto">
                                <RouterLink to="/educacao/eventos" editor={isStoryblokEditor}>
                                    <Button dark inline>
                                        {props?.button_text}
                                    </Button>
                                </RouterLink>
                            </Col>
                        </Row>
                        {contexto ? (
                            <Row>
                                <Col size="12" mt="2 3">
                                    <Text size="12 16" lineHeight="16 22" color={Colors.gray}>
                                        {render(props.description)}
                                    </Text>
                                </Col>
                            </Row>
                        ) : null}
                    </Col>
                    <Col size="12" pl={clickedSlider > 0 ? '1.5 0' : '1.5 4'} pr="1 0" pb="2 0">
                        <Row alignItems="stretch">
                            <SlickContainer index={clickedSlider}>
                                <Slider
                                    autoplay={false}
                                    dots={false}
                                    infinite={false}
                                    arrows
                                    arrowsColor={Colors.black}
                                    height={480}
                                    calendar
                                    clickedSlider={clickedSlider}
                                    handleClickedSlider={setClickedSlider}
                                >
                                    {content?.map((card, key) => (
                                        <React.Fragment key={card?._uid}>
                                            {key <= 6 && (
                                                <SlideItem height={480} mobileHeight={470} hoverEffect>
                                                    <ItemContainer color={renderColor(props.color || mainColor)}>
                                                        <Cards>
                                                            <StyledContentCard pt="3" pb="3" direction="column">
                                                                <Col size="auto" px="2.5" mb="3">
                                                                    <Text title size="88 112" lineHeight="80 100">
                                                                        {format(parseISO(card?.event_date), 'dd MMM', {
                                                                            locale: ptBR
                                                                        })}
                                                                    </Text>
                                                                </Col>
                                                                <Col size="auto" px="2.5" mb="1">
                                                                    <Text size="16" lineHeight="24" highlight>
                                                                        {card?.title}
                                                                    </Text>
                                                                </Col>
                                                                <Col size="auto" px="2.5">
                                                                    <Text body2 style={{ display: 'block' }}>
                                                                        {card?.event_format}
                                                                    </Text>
                                                                    <Text body2 style={{ display: 'block' }}>
                                                                        {card?.location}
                                                                    </Text>
                                                                </Col>
                                                            </StyledContentCard>
                                                            <a
                                                                href={card?.link?.url || card?.link?.cached_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <CardsButton
                                                                    color={renderColor(props.color || mainColor)}
                                                                    textColor={props.color || mainColor}
                                                                >
                                                                    {handleButtonContent(
                                                                        card?.inscription_start_date,
                                                                        card?.inscription_end_date
                                                                    )}
                                                                </CardsButton>
                                                            </a>
                                                        </Cards>
                                                    </ItemContainer>
                                                </SlideItem>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Slider>
                            </SlickContainer>
                        </Row>
                    </Col>
                    <Col size="12 hidden" px="1.5 1" mt="5">
                        <RouterLink to="/educacao/eventos" editor={isStoryblokEditor}>
                            <Button dark>{props?.button_text}</Button>
                        </RouterLink>
                    </Col>
                </Row>
            </Container>
        </MainContent>
    );
}

const MainContent = styled.div`
    position: relative;
    width: 100%;
    background-color: ${Colors.lightGrey};
`;

const SlickContainer = styled.div`
    position: relative;
    overflow: hidden;
    width: 100%;

    .slider-c {
        .slick-slider {
            > div:not(.slick-list) {
                transform: translate(0, -50%) scale(0.35);

                @media (min-width: 820px) {
                    transform: translate(0, -50%) scale(0.85);
                }
            }

            .slick-list {
                overflow: visible;
                width: 230px;

                @media (min-width: 820px) {
                    width: 263px;
                }

                .slick-track {
                    display: flex;
                    gap: 16px;
                    margin-left: 0;
                    overflow: visible;

                    @media (min-width: 820px) {
                        gap: 32px;
                        margin-left: ${({ index }) => (index === 1 ? '16px' : '-16px')};
                    }
                }
            }
        }
    }
`;

const ItemContainer = styled.div`
    position: relative;
    width: 230px;
    height: 100%;
    border-top: ${({ color }) => `8px solid ${color ?? Colors.educationGreen}`};

    @media (min-width: 768px) {
        width: 263px;
    }
`;

export const Cards = styled.div`
    width: 100%;
    height: 100%;
    background-color: ${Colors.white};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

export const CardsButton = styled.div`
    opacity: 1;
    width: 100%;
    height: 40px;
    padding: 9px 0;
    background-color: ${({ color }) => color ?? Colors.educationGreen};
    display: flex;
    justify-content: center;
    color: ${({ textColor }) => (textColor === 'orange' || textColor === 'yellow' ? Colors.white : Colors.black)};
    font-weight: bold;
    transition: opacity 200ms;
    user-select: none;

    &:hover {
        cursor: pointer;
        opacity: 0.8;
    }
`;

export const StyledContentCard = styled(Row)`
    flex-grow: 1;
    height: 100%;
`;
